import { AxiosResponse } from "axios";

import { APIHandler, EventSourceHandler, UnauthedAPIHandler } from "../../base";
import {
    TenantAdmin,
    TenantCrawlLimits,
    TenantAssistantOption,
    TenantAuthProvider,
    TenantAuthSettings,
    TenantDefaultAssistant,
    TenantDomainImpact
} from "../../../common/interfaces/admin/tenant.interface";
import type {
    SetProviderRequest,
    SsoTestResult
} from "../../../common/interfaces/admin/integration.interface";

export const fetchTenants = (search?: string) =>
    APIHandler.get(`/admin/tenants${search ? `?search=${encodeURIComponent(search)}` : ""}`);

export const createTenant = (data: { name: string; displayName: string; subdomain: string }) =>
    APIHandler.post("/admin/tenants", data);

export const deleteTenant = (companyId: number) => APIHandler.delete(`/admin/tenants/${companyId}`);

export const fetchAdminInviteLink = (companyId: number) =>
    APIHandler.get(`/admin/tenants/${companyId}/admin-invite`);

export const fetchTenantLicensing = (companyId: number) =>
    APIHandler.get(`/admin/tenants/${companyId}/licensing`);

export const updateTenantLicensing = (
    companyId: number,
    data: { enabled: boolean; startDate: string | null; limit: number | null }
) => APIHandler.patch(`/admin/tenants/${companyId}/licensing`, data);

export const fetchPermissionsCatalog = () => APIHandler.get("/admin/tenants/permissions-catalog");

export const fetchTenantCrawlLimits = (
    companyId: number
): Promise<AxiosResponse<TenantCrawlLimits>> =>
    APIHandler.get(`/admin/tenants/${companyId}/crawl-limits`);

export const updateTenantCrawlLimits = (
    companyId: number,
    crawlLimits: Record<string, number | null> | null
): Promise<AxiosResponse<TenantCrawlLimits>> =>
    APIHandler.patch(`/admin/tenants/${companyId}/crawl-limits`, { crawlLimits });

export const updateTenantPermissions = (
    companyId: number,
    permissions: Record<string, boolean> | null
) => APIHandler.patch(`/admin/tenants/${companyId}/permissions`, { permissions });

/** Super admin: the tenant's admins with their per-user grants (delegation design §5). */
export const fetchTenantAdmins = (companyId: number) =>
    APIHandler.get<{ items: TenantAdmin[] }>(`/admin/tenants/${companyId}/admins`);

/** Super admin: replace who holds a per-user permission. */
export const replacePermissionGrantees = (companyId: number, key: string, userUids: string[]) =>
    APIHandler.put(`/admin/tenants/${companyId}/permissions/${key}/grantees`, { userUids });

export const fetchTenantAuth = (companyId: number) =>
    APIHandler.get(`/admin/tenants/${companyId}/auth`);

export const updateTenantAuth = (companyId: number, authSettings: TenantAuthSettings | null) =>
    APIHandler.patch(`/admin/tenants/${companyId}/auth`, { authSettings });

/** Super admin: open a support session into a tenant — returns { url, expiresAt }. */
export const createSupportSession = (companyId: number) =>
    APIHandler.post(`/admin/tenants/${companyId}/support-session`);

/** Public: sign-in methods the login page should offer for a tenant origin. */
export const fetchTenantAuthOptions = (tenantOrigin?: string) =>
    UnauthedAPIHandler.get("/admin/tenants/auth-options", {
        params: tenantOrigin ? { tenantOrigin } : undefined
    });

/** Public: redeem a single-use support-session code — returns { customToken, tenantId }. */
export const redeemSupportSession = (code: string) =>
    UnauthedAPIHandler.post("/admin/tenants/support-session/redeem", { code });

export const subscribeTenantUpdates = () => EventSourceHandler("/admin/tenants/sse");

export const fetchTenantAuthImpact = (companyId: number, domains: string[]) =>
    APIHandler.get<TenantDomainImpact>(`/admin/tenants/${companyId}/auth/impact`, {
        params: { domains: domains.join(",") }
    });

/** Microsoft directory pinning (§2.6): called right after a Microsoft popup. */
export const confirmMicrosoftDirectory = (body: {
    firebaseIdToken: string;
    microsoftIdToken: string;
}) =>
    UnauthedAPIHandler.post<{ pinned: boolean; directoryId?: string }>(
        "/admin/tenants/auth/microsoft/confirm-directory",
        body
    );

/**
 * Login page, after any sign-in popup: apply the tenant policy to the fresh
 * token now (204), or get the guard's 403 { code } while the Firebase SDK
 * session is still here. Sent outside APIHandler so its interceptor doesn't
 * treat the refusal as a session to tear down.
 */
export const precheckSignIn = (firebaseIdToken: string) =>
    UnauthedAPIHandler.post<void>("/admin/tenants/auth/precheck", undefined, {
        headers: { Authorization: `Bearer ${firebaseIdToken}` }
    });

// --- Main-app SSO providers (tenant_authentication_plan.md Phase 4) --------

const providersPath = (companyId: number) => `/admin/tenants/${companyId}/auth/providers`;

export const fetchTenantAuthProviders = (companyId: number) =>
    APIHandler.get<TenantAuthProvider[]>(providersPath(companyId));

/** Step 1: the row (type + button label). */
export const createTenantAuthProvider = (
    companyId: number,
    data: { providerType: "saml" | "oidc"; displayName: string }
) => APIHandler.post<TenantAuthProvider>(providersPath(companyId), data);

/** Step 2: IdP details → Firebase (never stored). Clears any earlier test. */
export const configureTenantAuthProvider = (
    companyId: number,
    id: string,
    data: SetProviderRequest
) => APIHandler.put<TenantAuthProvider>(`${providersPath(companyId)}/${id}/config`, data);

export const updateTenantAuthProvider = (
    companyId: number,
    id: string,
    data: { displayName?: string; isEnabled?: boolean }
) => APIHandler.patch<TenantAuthProvider>(`${providersPath(companyId)}/${id}`, data);

export const deleteTenantAuthProvider = (companyId: number, id: string) =>
    APIHandler.delete(`${providersPath(companyId)}/${id}`);

export const fetchTenantAuthProviderSpConfig = (companyId: number, id: string) =>
    APIHandler.get(`${providersPath(companyId)}/${id}/sp-config`);

export const fetchTenantAuthProviderSpMetadataXml = (companyId: number, id: string) =>
    APIHandler.get(`${providersPath(companyId)}/${id}/sp-metadata.xml`, {
        responseType: "text"
    });

export const testTenantAuthProviderSso = (companyId: number, id: string, idToken: string) =>
    APIHandler.post<SsoTestResult>(`${providersPath(companyId)}/${id}/test-sso`, { idToken });

// --- Default Assistants for automatic user creation (Phase 5) -------------

/** Super admin: the tenant's Assistants for the picker (hidden ones flagged). */
export const fetchTenantAssistants = (companyId: number) =>
    APIHandler.get<TenantAssistantOption[]>(`/admin/tenants/${companyId}/assistants`);

export const fetchTenantDefaultAssistants = (companyId: number) =>
    APIHandler.get<TenantDefaultAssistant[]>(`/admin/tenants/${companyId}/auth/default-assistants`);

/** Replaces the whole list; order = display order, first = starting Assistant. */
export const replaceTenantDefaultAssistants = (companyId: number, knowledgeBaseUids: string[]) =>
    APIHandler.put<TenantDefaultAssistant[]>(
        `/admin/tenants/${companyId}/auth/default-assistants`,
        {
            knowledgeBaseUids
        }
    );
